import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';

/**
 * Gates the consumer migration from `@react-google-maps/api` onto
 * `@vis.gl/react-google-maps`. Flag OFF keeps today's exact loader / component /
 * autocomplete data-source paths so we can revert instantly; flag ON selects the
 * new vis.gl paths. Mirrors clowncar's `CLOWNCAR_ENABLE_GOOGLE_MAPS_API_UPDATE`.
 *
 * TODO(visgl-burn-in): central cleanup checklist. Once
 * `CONSUMER_ENABLE_GOOGLE_MAPS_API_UPDATE` is fully rolled out and stable, delete
 * this hook and the legacy (`@react-google-maps/api`) branches it gates. Every
 * item below carries its own inline `TODO(visgl-burn-in)` marker
 * (`grep -rn "visgl-burn-in" consumer/` finds them all). At burn-in:
 *
 * 1. Delete this file and every `useIsVisglMapsEnabled()` / `useNewApi` /
 *    `useVisglMaps` call site; keep only the flag-ON (vis.gl) code path.
 *
 * 2. Source — remove the legacy branch + the flag fork, keep and un-suffix the
 *    `*New` sibling:
 *      - components/maps/ConsumerMap.tsx (legacy `GoogleMap` branch; keep ConsumerMapNew)
 *      - components/maps/ConsumerMarker.tsx (legacy `Marker` branch; keep ConsumerMarkerNew)
 *      - components/maps/ConsumerLocationDot.tsx (legacy `Marker` branch; keep ConsumerLocationDotNew)
 *      - components/maps/LoadGoogleMaps.tsx (legacy loader branch)
 *      - components/maps/useLoadGoogleMaps.ts (legacy `useLoadScript`)
 *      - components/profile/culinary-travel-log/ProfileMap.tsx (legacy branch; keep ProfileMapNew)
 *      - components/profile/culinary-travel-log/RestaurantMarker.tsx (whole file; keep RestaurantMarkerNew)
 *      - components/business/landing-page/BusinessMapWithScript.tsx (`BusinessMap` +
 *        `LegacyBusinessMapWithScript` + fork wrapper; keep `BusinessMapEmbed`)
 *      - components/inputs/LocationSuggestField.tsx (`useNewApi`/`useVisglMaps` fork)
 *      - components/patron-location/PatronLocationFinder.tsx (fork)
 *      - components/patron-location/PatronLocationFinderSearchInput.tsx (fork)
 *      - forms/EditDeliveryAddressForm.tsx (fork)
 *      - utils/deliveryUtils.ts (`useNewApi` param)
 *      - utils/locationUtils.ts (`useNewApi` params + legacy branches; keep `fetchModern*`)
 *      - constants/googleMapConstants.ts (deep `@react-google-maps/api/dist/...` import)
 *
 * 3. Dependency — drop `@react-google-maps/api` from consumer/package.json once
 *    nothing imports it.
 *
 * 4. Tests — delete the legacy-only blocks/files:
 *      - test/.../maps/ConsumerMap.test.tsx, ConsumerLocationDot.test.tsx,
 *        LoadGoogleMaps.test.tsx, profile/culinary-travel-log/ProfileMap.test.tsx
 *        (the "flag is off" cases)
 *      - test/.../maps/ConsumerMarker.test.tsx (legacy `jest.mock` + non-flag-ON cases)
 *      - test/.../profile/culinary-travel-log/RestaurantMarker.test.tsx (whole file)
 *      - test/.../utils/locationUtilsPlaces.test.ts (the legacy flag-OFF `describe` only;
 *        keep modern + getDirections)
 *      - test/.../business/landing-page/BusinessMapWithScript.test.tsx (the "flag is off" case)
 *      - test/client/testutils/googleMapsMocks.tsx (`createReactGoogleMapsApiMock` +
 *        classic-only fakes)
 */
export function useIsVisglMapsEnabled(): boolean {
  return useFeatureFlagForConsumer('CONSUMER_ENABLE_GOOGLE_MAPS_API_UPDATE');
}
